import type { BuildingBounds } from "building-types/building/buildingBounds";
import type { DocEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { Level } from "building-types/levels/levelTypes";
import type { ExteriorMaterial } from "building-types/materials/exteriorMaterialTypes";
import type { ExteriorMaterialOverride } from "building-types/materials/materialTypes";
import type { Room } from "building-types/rooms/roomTypes";
import { SiteResolutionLevel } from "building-types/site/site";
import { type GeneratedSite, type Site } from "building-types/site/site";
import type { SiteGeomTagGroup } from "building-types/site/siteGeomTagGroup";
import {
  type ConformalShape,
  type SiteShape,
  SiteShapeType,
  isConformalShape,
  isHeightBasedShape,
} from "building-types/site/siteShape";
import type { Table } from "common/data/table";
import { exteriorMaterialOverrideNew } from "doc/utils/materialTools";
import { generatedSiteNew } from "doc/utils/siteTools";
import type { BRep } from "geom/brep";
import { brepNew, faceNew, faceTopoNew, verticalFacesByPolyLine3AndBottomZ } from "geom/brep";
import GeomTagGroup from "geom/geomTagGroup";
import { gridLoft } from "geom/gridLoft";
import { planeNewXY } from "geom/plane";
import {
  polyLine2SetCW,
  polyLine2Copy,
  polyLine2ReflectOverYAxis,
  polyLine2Move,
  polyLine2Reverse,
  polyLine2Shift,
  polyLine2IsClosed,
} from "geom/polyLine2";
import { type PolyLine2 } from "geom/polyLine2Types";
import { type PolyLine3 } from "geom/polyLine3";
import { rect2ByTwoPoints } from "geom/rect2";
import { subtractOutlineFromFacesAndExtrudeToBaseHeight } from "./siteShapeUtils";
import {
  collectSubtractionOutlines,
  createExtrusionBRep as extrusionToBRep,
  createOutlineBRep,
  createTerrainSurfaceBRep,
  intersectBRepWithOutline,
  processContourShape,
  processLandscapeShape,
  processRetainingWallShape,
  processSiteFillShape,
  processSiteOverrideShape,
  processSiteSubtractShape,
} from "./siteShapeUtils";
import type { SiteZGeom } from "./siteUtils";
import { calculateRelativeHeight, siteShapePickHandle } from "./siteUtils";

const SITE_THICKNESS = 300;

interface MakeContourSiteOptions {
  readonly site: Site;
  readonly buildingBounds: BuildingBounds;
  readonly levels: Table<Level>;
  readonly siteShapes: SiteShape[];
  readonly rooms: Table<Room>;
  readonly roomsByLevel: Table<string[]>;
  readonly mirror: boolean;
  readonly mirrorTranslationX: number;
}

function getSiteResolution(siteResolution: SiteResolutionLevel): number {
  switch (siteResolution) {
    case SiteResolutionLevel.COARSE:
      return 72;
    case SiteResolutionLevel.MEDIUM:
      return 48;
    case SiteResolutionLevel.FINE:
      return 36;
    default:
      return 72;
  }
}

function mirrorPolyLine2ForSite(pl: PolyLine2, mirrorTranslationX: number): PolyLine2 {
  let res = polyLine2ReflectOverYAxis(polyLine2Copy(pl));
  if (polyLine2IsClosed(res)) {
    res = polyLine2Shift(polyLine2Reverse(res), 1);
  } else {
    res = polyLine2Reverse(res);
  }
  polyLine2Move(res, mirrorTranslationX, 0);
  return res;
}

export function makeContourSite(options: MakeContourSiteOptions): GeneratedSite | undefined {
  const { site, buildingBounds, siteShapes, levels, rooms, roomsByLevel, mirror, mirrorTranslationX } = options;
  const siteElevationReference = site.siteBaseElevation ?? 0;

  if (!buildingBounds?.aabb) {
    return undefined;
  }

  // Apply mirroring to site shapes when the building is mirrored. We create shallow
  // copies so the original Doc data remains untouched.
  const processedSiteShapes: SiteShape[] = mirror
    ? siteShapes.map((shape) => ({
        ...shape,
        geom: mirrorPolyLine2ForSite(shape.geom, mirrorTranslationX),
      }))
    : siteShapes;

  const generatedSite = generatedSiteNew(site);
  generatedSite.siteShapes.push(...processedSiteShapes);

  const contourPolyLines: PolyLine3[] = [];
  const heightOverridePls: PolyLine3[] = [];
  const siteFillPolyLines: PolyLine3[] = [];
  const siteSubtractPolyLines: PolyLine3[] = [];
  const landscapePolyLines: PolyLine3[] = [];

  let heightOffset = 0;

  for (const shape of processedSiteShapes) {
    if (!isHeightBasedShape(shape) || (isConformalShape(shape) && shape.isConformal)) {
      continue;
    }

    switch (shape.type) {
      case SiteShapeType.CONTOUR:
        processContourShape(shape, contourPolyLines, siteElevationReference);
        break;
      case SiteShapeType.WALK:
        heightOffset = calculateRelativeHeight(shape.zHeight ?? 0, siteElevationReference);
        const walkBRep = createOutlineBRep(shape.geom, heightOffset, 6);
        const walkFill = createOutlineBRep(shape.geom, heightOffset - 6, SITE_THICKNESS);
        if (walkBRep && walkFill) {
          generatedSite.grassGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              walkFill,
              exteriorMaterialOverrideNew(site.surfaceMaterial),
            ),
            pickHandle: siteShapePickHandle(shape, options.site.id),
          });
          generatedSite.sidewalkGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              walkBRep,
              exteriorMaterialOverrideNew(shape.material),
            ),
            pickHandle: siteShapePickHandle(shape, options.site.id),
          });
        }
        break;
      case SiteShapeType.DRIVEWAY:
        const drivewayHeight = 2;
        heightOffset = calculateRelativeHeight(shape.zHeight ?? 0, siteElevationReference);
        const drivewayBRep = createOutlineBRep(shape.geom, heightOffset, 12);
        const drivewayFill = createOutlineBRep(shape.geom, heightOffset - drivewayHeight, SITE_THICKNESS);
        if (drivewayBRep && drivewayFill) {
          generatedSite.drivewayGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              drivewayBRep,
              exteriorMaterialOverrideNew(shape.material),
            ),
            pickHandle: siteShapePickHandle(shape, options.site.id),
          });
          generatedSite.grassGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              drivewayFill,
              exteriorMaterialOverrideNew(site.surfaceMaterial),
            ),
            pickHandle: siteShapePickHandle(shape, options.site.id),
          });
        }
        break;
      case SiteShapeType.ROAD:
        heightOffset = calculateRelativeHeight(shape.zHeight ?? 0, siteElevationReference);
        const roadBRep = createOutlineBRep(shape.geom, heightOffset, 6);
        if (roadBRep) {
          generatedSite.roadGeom.push({
            group: new GeomTagGroup<ExteriorMaterialOverride>().add(
              roadBRep,
              exteriorMaterialOverrideNew(shape.material),
            ),
            pickHandle: siteShapePickHandle(shape, options.site.id),
          });
        }
        break;
      case SiteShapeType.RETAINING_WALL:
        processRetainingWallShape(shape, generatedSite, siteElevationReference);
        break;
      case SiteShapeType.SITE_FILL:
        processSiteFillShape(shape, siteFillPolyLines, generatedSite, siteElevationReference);
        break;
      case SiteShapeType.SITE_SUBTRACT:
        processSiteSubtractShape(shape, siteSubtractPolyLines, generatedSite, siteElevationReference);
        break;
      case SiteShapeType.SITE_OVERRIDE:
        processSiteOverrideShape(shape, heightOverridePls, siteElevationReference);
        break;
    }
  }

  const subtractionOutline = collectSubtractionOutlines(processedSiteShapes, levels, rooms, roomsByLevel);
  const siteSubtractionBRep = extrusionToBRep(subtractionOutline, -1000, 2000);

  let bottomContourZ = 0;

  if (contourPolyLines.length > 0) {
    bottomContourZ = contourPolyLines.sort((a, b) => a[2] - b[2])[0][2];
  }

  const sitePlane = faceNew(
    [polyLine2SetCW(rect2ByTwoPoints(-50000, -50000, 50000, 50000))],
    planeNewXY(0, 0, bottomContourZ - 100),
  );

  generatedSite.sitePlaneGeom.add(sitePlane, exteriorMaterialOverrideNew("foundation0", undefined));

  // Create initial terrain BRep from lofted faces
  const {
    faces: loftedFaces,
    edgePl3s,
    lowestZ,
  } = gridLoft(
    contourPolyLines,
    heightOverridePls,
    getSiteResolution(site.siteResolution ?? SiteResolutionLevel.MEDIUM),
  );
  let currentTerrainBRep = createTerrainSurfaceBRep(loftedFaces);
  const currentTerrainGeom: SiteZGeom = {
    geometry: currentTerrainBRep,
  };

  // Process site shapes that require terrain data
  for (const shape of processedSiteShapes) {
    if (shape.type === SiteShapeType.LANDSCAPE) {
      processLandscapeShape(shape, landscapePolyLines, generatedSite, currentTerrainGeom, siteElevationReference);
    }

    if (shape.type === SiteShapeType.ROAD && shape.isConformal) {
      processConformalShape(shape, currentTerrainBRep, site, generatedSite.roadGeom);
    }

    if (shape.type === SiteShapeType.DRIVEWAY && shape.isConformal) {
      processConformalShape(shape, currentTerrainBRep, site, generatedSite.drivewayGeom);
    }

    if (shape.type === SiteShapeType.WALK && shape.isConformal) {
      processConformalShape(shape, currentTerrainBRep, site, generatedSite.sidewalkGeom);
    }
  }

  /**
   * These "magic" numbers are the closest I could reasonably get to parity with the existing system
   * given the differences. The previous system extruded each mesh triangle relatively... and the new
   * version has a fixed base. So this finds the lowest z value in the loft grid points and extrudes
   * about the same height as before down.
   *
   * Product should be able to define the appropriate subtraction height here... and when that happens
   * the engineer should delete this comment.
   */
  const subtractionHeight = lowestZ - SITE_THICKNESS * 2;
  if (currentTerrainBRep && siteSubtractionBRep) {
    const { faces, intersected } = subtractOutlineFromFacesAndExtrudeToBaseHeight(
      currentTerrainBRep.faces,
      subtractionOutline,
      subtractionHeight,
    );

    if (faces.length > 0) {
      for (const edge of edgePl3s) {
        faces.push(...verticalFacesByPolyLine3AndBottomZ(edge, subtractionHeight));
      }

      currentTerrainBRep = createTerrainSurfaceBRep(faces);
    }

    if (intersected.length > 0) {
      generatedSite.grassGeom.push({
        group: new GeomTagGroup<ExteriorMaterialOverride>().addMany(intersected, exteriorMaterialOverrideNew("earth0")),
        pickHandle: { type: "SITE", id: site.id },
      });
    }
  }

  // Add all geometry to their respective collections
  if (currentTerrainBRep) {
    for (const face of currentTerrainBRep.faces) {
      generatedSite.topographyGeom.add(face, exteriorMaterialOverrideNew(site.surfaceMaterial));
    }
  }

  return generatedSite;
}

// Helper function to push geometry to SiteGeomTagGroups
function pushSiteGeom(
  target: SiteGeomTagGroup[],
  brep: BRep,
  material: ExteriorMaterial | undefined,
  pickHandle: DocEntityHandle | undefined,
): void {
  const group = new GeomTagGroup<ExteriorMaterialOverride>().add(brep, exteriorMaterialOverrideNew(material));
  target.push({ group, pickHandle });
}

/**
 * Processes a conformal site shape by intersecting its geometry with the current terrain,
 * creating a BRep with face topologies, and pushing the result to the provided geometry collection.
 *
 * @param shape The site shape to process.
 * @param currentTerrainBRep The current terrain BRep to intersect with.
 * @param site The site instance to retrieve a default integer ID.
 * @param target The target geometry collection (e.g., generatedSite.roadGeom, drivewayGeom, or sidewalkGeom) where the result will be pushed.
 */
function processConformalShape(
  shape: ConformalShape,
  currentTerrainBRep: BRep,
  site: Site,
  target: SiteGeomTagGroup[],
): void {
  if (!shape.isConformal) {
    return;
  }
  const conformalSurface = intersectBRepWithOutline(currentTerrainBRep, shape.geom);
  if (!conformalSurface) {
    return;
  }
  const faceTopos = conformalSurface.map(() => faceTopoNew());
  const conformalBRep = brepNew(conformalSurface, faceTopos);
  pushSiteGeom(target, conformalBRep, shape.material, siteShapePickHandle(shape, site.id));
}
